import { useState } from "react";
import { toast } from "@/hooks/use-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, Globe, Clock, BarChart3, Activity, Loader2, AlertCircle, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Filter } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { useWebsiteCategories } from "@/hooks/useWebsiteCategories";
import { fetchAllAnalytics, updateUrlEntry, type AnalyticsEntry } from "@/lib/upstash";

const simplifyUrl = (url: string): string => {
  try {
    // Remove https://, http://, and www. (case-insensitive)
    return url
      .replace(/^https?:\/\//i, "")
      .replace(/^www\./i, "");
  } catch {
    return url;
  }
};

const formatLatency = (ms: number): string => {
  return (ms / 1000).toFixed(3) + "s";
};

const getLatencyColor = (ms: number): string => {
  if (ms < 500) return "text-green-600";
  if (ms < 1000) return "text-yellow-600";
  return "text-red-600";
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export const BrowserAnalyticsSection = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [showUnverifiedOnly, setShowUnverifiedOnly] = useState(false);
  const queryClient = useQueryClient();
  const { categories } = useWebsiteCategories();

  // Get flat list of category names, sorted alphabetically
  const categoryNames = categories.map(cat => cat.name).sort((a, b) => a.localeCompare(b));

  const updateEntryMutation = useMutation({
    mutationFn: async ({ url, verified, category }: { url: string; verified?: boolean; category?: string }) => {
      await updateUrlEntry({ url, verified, category });
      return { url, verified, category };
    },
    onSuccess: ({ url, verified, category }) => {
      // Optimistically update the cache
      queryClient.setQueryData(['browser-analytics'], (oldData: AnalyticsEntry[] | undefined) => {
        if (!oldData) return oldData;
        return oldData.map(entry => {
          if (entry.url !== url) return entry;
          
          if (category !== undefined && category !== entry.category) {
            // Category changed - store old category in verified
            return { ...entry, category, verified: entry.category };
          } else if (verified !== undefined) {
            return { ...entry, verified };
          }
          return entry;
        });
      });
    },
  });

  const handleVerifiedChange = (url: string, checked: boolean) => {
    updateEntryMutation.mutate({ url, verified: checked });
    if (checked) {
      toast({
        title: "URL Verified",
        description: "This URL has been marked as verified.",
      });
    }
  };

  const handleCategoryChange = (url: string, newCategory: string, currentCategory: string) => {
    if (newCategory !== currentCategory) {
      updateEntryMutation.mutate({ url, category: newCategory });
    }
  };

  const { data: analyticsData, isLoading, error } = useQuery({
    queryKey: ['browser-analytics'],
    queryFn: fetchAllAnalytics,
    staleTime: 30000,
    refetchInterval: 60000,
  });

  const entries = analyticsData || [];

  const filteredData = entries.filter((entry) => {
    const matchesSearch = simplifyUrl(entry.url).toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.category.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesVerifiedFilter = !showUnverifiedOnly || entry.verified === false;
    return matchesSearch && matchesVerifiedFilter;
  });

  // Reset to page 1 when search changes
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleUnverifiedFilterChange = () => {
    setShowUnverifiedOnly(prev => !prev);
    setCurrentPage(1);
  };

  // Reset to page 1 when page size changes
  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setCurrentPage(1);
  };

  // Pagination calculations
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const totalRequests = entries.reduce((sum, entry) => sum + entry.count, 0);
  const avgLatency = entries.length > 0 
    ? entries.reduce((sum, entry) => sum + entry.latency, 0) / entries.length 
    : 0;
  const verifiedCount = entries.filter((entry) => entry.verified).length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading analytics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-2">
        <AlertCircle className="h-8 w-8 text-destructive" />
        <p className="text-destructive font-medium">Failed to load analytics</p>
        <p className="text-sm text-muted-foreground">{error instanceof Error ? error.message : 'Unknown error'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="py-3 px-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Globe className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total URLs</p>
                <p className="text-xl font-bold">{entries.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="py-3 px-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <BarChart3 className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Requests</p>
                <p className="text-xl font-bold">{totalRequests.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="py-3 px-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-4 w-4 text-yellow-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Avg Latency</p>
                <p className="text-xl font-bold">{formatLatency(avgLatency)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="py-3 px-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Activity className="h-4 w-4 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Verified</p>
                <p className="text-xl font-bold">{verifiedCount}/{entries.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>URL Analytics</CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant={showUnverifiedOnly ? "default" : "outline"}
                size="sm"
                onClick={handleUnverifiedFilterChange}
                className="gap-2"
              >
                <Filter className="h-4 w-4" />
                Unverified Only
              </Button>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search URLs or categories..."
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="w-full overflow-x-auto">
            <Table className="w-full min-w-[700px]">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[35%]">URL</TableHead>
                  <TableHead className="w-[12%]">Latency</TableHead>
                  <TableHead className="w-[25%]">Category</TableHead>
                  <TableHead className="w-[13%]">Verified</TableHead>
                  <TableHead className="w-[15%] text-right">Count</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedData.map((entry, index) => (
                  <TableRow key={`${entry.url}-${startIndex + index}`}>
                    <TableCell className="font-mono text-sm max-w-[300px] truncate">
                      {simplifyUrl(entry.url)}
                    </TableCell>
                    <TableCell>
                      <span className={`font-medium ${getLatencyColor(entry.latency)}`}>
                        {formatLatency(entry.latency)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Select 
                        value={entry.category} 
                        onValueChange={(value) => handleCategoryChange(entry.url, value, entry.category)}
                      >
                        <SelectTrigger className="w-[180px] h-8">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categoryNames.length > 0 ? (
                            categoryNames.map((cat) => (
                              <SelectItem key={cat} value={cat}>
                                {cat}
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem value={entry.category}>
                              {entry.category}
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="py-2">
                      {typeof entry.verified === 'string' ? (
                        <Badge variant="outline" className="text-xs whitespace-nowrap">
                          {entry.verified}
                        </Badge>
                      ) : (
                        <Checkbox 
                          checked={entry.verified === true} 
                          onCheckedChange={(checked) => handleVerifiedChange(entry.url, checked === true)}
                        />
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {entry.count.toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
                {paginatedData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                      {entries.length === 0 ? 'No analytics data available' : 'No results found'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination Controls */}
          {totalItems > 0 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>Showing {startIndex + 1}-{endIndex} of {totalItems} entries</span>
                <div className="flex items-center gap-2 ml-4">
                  <span>Rows per page:</span>
                  <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                    <SelectTrigger className="w-[70px] h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZE_OPTIONS.map((size) => (
                        <SelectItem key={size} value={String(size)}>
                          {size}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm px-3">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
